import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Clock, MapPin, Phone, Mail } from 'lucide-react'
import { PageSeo, HeroSimple, Breadcrumbs, BookingForm } from '@/components/ui'
import { LocalizedLink } from '@/components/LocalizedLink'
import type { RouteKey } from '@/i18n/routes'

const serviceKeys: RouteKey[] = [
  'breastSurgery',
  'bodySurgery',
  'faceSurgery',
  'bodyAesthetic',
  'faceAesthetic',
  'regenerative',
  'laser',
]

const openingDays: { key: string; labelKey: string; hoursKey: string }[] = [
  { key: 'monday', labelKey: 'monday', hoursKey: 'monday' },
  { key: 'tuesday', labelKey: 'tuesday', hoursKey: 'tuesday' },
  { key: 'wednesday', labelKey: 'wednesday', hoursKey: 'wednesday' },
  { key: 'thursday', labelKey: 'thursday', hoursKey: 'thursday' },
  { key: 'friday', labelKey: 'friday', hoursKey: 'friday' },
  { key: 'saturday', labelKey: 'saturday', hoursKey: 'saturday' },
  { key: 'sunday', labelKey: 'sunday', hoursKey: 'sunday' },
]

export function BookingPage() {
  const { t, i18n } = useTranslation(['booking', 'common'])

  const serviceOptions = useMemo(
    () =>
      serviceKeys.map((key) => ({
        value: key,
        label: t(`common:routes.${key}`),
      })),
    [t]
  )

  const jsonLd = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'MedicalBusiness',
        name: 'AD Aesthetics',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Via Gazzera, 1',
          addressLocality: 'Verona',
          postalCode: '37128',
          addressCountry: 'IT',
        },
        telephone: '+390458348902',
        email: 'info@adaesthetics.it',
        openingHoursSpecification: [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            opens: '09:00',
            closes: '19:00',
          },
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Saturday', 'Sunday'],
            closes: '00:00',
            opens: '00:00',
          },
        ],
      },
    ],
    []
  )

  return (
    <>
      <PageSeo
        title={t('booking:seo.title')}
        description={t('booking:seo.description')}
        type="website"
        jsonLd={jsonLd}
        breadcrumb={[
          { name: t('common:routes.home'), path: `/${i18n.language}` },
          { name: t('common:routes.bookConsultation') },
        ]}
      />

      <main className="min-h-screen bg-ink-deep">
        <HeroSimple
          title={t('booking:hero.title')}
          subtitle={t('booking:hero.subtitle')}
          variant="dark"
        />

        <Breadcrumbs
          items={[
            { label: t('common:routes.home'), to: 'home' },
            { label: t('common:routes.bookConsultation') },
          ]}
        />

        <section className="py-16 md:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
              {/* Form */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.5 }}
                className="lg:col-span-7"
              >
                <BookingForm serviceOptions={serviceOptions} />
              </motion.div>

              {/* Sidebar */}
              <motion.aside
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="lg:col-span-5 space-y-6"
              >
                {/* Opening hours */}
                <div className="rounded-2xl border border-white/10 bg-ink p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                      <Clock className="w-5 h-5" />
                    </div>
                    <h3 className="font-display text-xl text-white">
                      {t('booking:info.title')}
                    </h3>
                  </div>

                  <ul className="space-y-3">
                    {openingDays.map((day) => (
                      <li
                        key={day.key}
                        className="flex items-center justify-between text-sm border-b border-white/5 pb-3 last:border-0 last:pb-0"
                      >
                        <span className="text-white/70">
                          {t(`booking:info.${day.labelKey}`, {
                            ns: 'booking',
                            defaultValue: day.key,
                          })}
                        </span>
                        <span
                          className={
                            t(`booking:openingHours.${day.hoursKey}`) === t('booking:info.closed')
                              ? 'text-white/40'
                              : 'text-primary font-medium'
                          }
                        >
                          {t(`booking:openingHours.${day.hoursKey}`)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Contacts */}
                <div className="rounded-2xl border border-primary/20 bg-primary/5 p-6 md:p-8">
                  <h3 className="font-display text-xl text-white mb-6">
                    {t('booking:info.contactsTitle')}
                  </h3>

                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                      <span className="text-white/80">{t('booking:info.address')}</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-primary shrink-0" />
                      <a
                        href={`tel:${t('common:contact.phone').replace(/\s/g, '')}`}
                        className="text-white/80 hover:text-primary transition-colors"
                      >
                        {t('common:contact.phone')}
                      </a>
                    </li>
                    <li className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-primary shrink-0" />
                      <a
                        href={`mailto:${t('common:contact.email')}`}
                        className="text-white/80 hover:text-primary transition-colors"
                      >
                        {t('common:contact.email')}
                      </a>
                    </li>
                  </ul>

                  <div className="mt-6 pt-6 border-t border-white/10">
                    <LocalizedLink
                      to="contacts"
                      className="inline-flex items-center text-sm font-medium text-primary hover:text-primary-light transition-colors"
                    >
                      {t('common:buttons.contactUs')}
                    </LocalizedLink>
                  </div>
                </div>
              </motion.aside>
            </div>
          </div>
        </section>
      </main>
    </>
  )
}
